/**
 * Investor-grade cohort retention heatmap.
 *
 * Rows:    signup cohorts (most recent on top)
 * Columns: week offset 0..N
 * Cell:    retention % (colored via sequential Cedar ramp)
 * Hover:   full cohort_week, week_offset, cohort_size, active_users, pct
 *
 * Built with visx for layout control. Uses ResponsiveContainer so the chart
 * reflows to the card width.
 */
import React, { useMemo, useState } from 'react';
import { Group } from '@visx/group';
import { scaleLinear, scaleBand } from '@visx/scale';
import { ParentSize } from '@visx/responsive';
import { Tooltip, defaultStyles, useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { SEQUENTIAL } from '../theme';
import { formatInt, formatPercent } from '../format';

export interface CohortCell {
  week_offset: number;
  active_users: number;
  retention_pct: number;
}

export interface CohortRow {
  cohort_week: string; // 'YYYY-MM-DD' (Monday of signup week)
  cohort_size: number;
  cells: CohortCell[];
}

export interface CohortHeatmapProps {
  cohorts: CohortRow[];
  maxWeekOffset?: number;
  height?: number;
}

const MARGIN = { top: 32, right: 16, bottom: 8, left: 92 };

// Interpolate between the SEQUENTIAL palette stops for any 0..1 value.
function retentionColor(pct: number): string {
  const t = Math.max(0, Math.min(1, pct / 100));
  const steps = SEQUENTIAL.length - 1;
  const idx = t * steps;
  const lo = Math.floor(idx);
  const hi = Math.min(steps, lo + 1);
  const frac = idx - lo;
  return mixHex(SEQUENTIAL[lo], SEQUENTIAL[hi], frac);
}

function mixHex(a: string, b: string, t: number): string {
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

function formatCohortLabel(iso: string): string {
  // 'YYYY-MM-DD' → 'MMM D'
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface TooltipDatum {
  cohort_week: string;
  cohort_size: number;
  week_offset: number;
  active_users: number;
  retention_pct: number;
}

export const CohortHeatmap: React.FC<CohortHeatmapProps> = ({
  cohorts,
  maxWeekOffset,
  height = 320,
}) => {
  const orderedCohorts = useMemo(
    () => [...cohorts].sort((a, b) => (a.cohort_week < b.cohort_week ? 1 : -1)),
    [cohorts],
  );

  const maxOffset = useMemo(() => {
    if (maxWeekOffset != null) return maxWeekOffset;
    let m = 0;
    for (const row of cohorts) {
      for (const cell of row.cells) if (cell.week_offset > m) m = cell.week_offset;
    }
    return Math.max(m, 12);
  }, [cohorts, maxWeekOffset]);

  const weekOffsets = useMemo(
    () => Array.from({ length: maxOffset + 1 }, (_, i) => i),
    [maxOffset],
  );

  if (!cohorts.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No cohorts yet. Users need to sign up and return for retention to populate.
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ParentSize>
        {({ width }) => (
          <HeatmapInner
            width={width}
            height={height}
            cohorts={orderedCohorts}
            weekOffsets={weekOffsets}
          />
        )}
      </ParentSize>
    </div>
  );
};

interface InnerProps {
  width: number;
  height: number;
  cohorts: CohortRow[];
  weekOffsets: number[];
}

const HeatmapInner: React.FC<InnerProps> = ({ width, height, cohorts, weekOffsets }) => {
  const innerW = Math.max(100, width - MARGIN.left - MARGIN.right);
  const innerH = Math.max(100, height - MARGIN.top - MARGIN.bottom);

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        domain: weekOffsets,
        range: [0, innerW],
        padding: 0.08,
      }),
    [weekOffsets, innerW],
  );

  const yScale = useMemo(
    () =>
      scaleBand<string>({
        domain: cohorts.map((c) => c.cohort_week),
        range: [0, innerH],
        padding: 0.12,
      }),
    [cohorts, innerH],
  );

  const cellW = xScale.bandwidth();
  const cellH = yScale.bandwidth();

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipDatum>();

  return (
    <div className="relative h-full w-full">
      <svg width={width} height={height}>
        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* X axis labels (week offsets) */}
          {weekOffsets.map((wo) => (
            <text
              key={wo}
              x={(xScale(wo) ?? 0) + cellW / 2}
              y={-10}
              fontSize={10}
              fontFamily="Inter, system-ui, sans-serif"
              fill="var(--text-secondary)"
              textAnchor="middle"
            >
              W{wo}
            </text>
          ))}

          {/* Y axis labels (cohort weeks) + cohort size */}
          {cohorts.map((row) => (
            <g key={row.cohort_week}>
              <text
                x={-8}
                y={(yScale(row.cohort_week) ?? 0) + cellH / 2}
                fontSize={11}
                fontWeight={600}
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--text-strong)"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {formatCohortLabel(row.cohort_week)}
              </text>
              <text
                x={-8}
                y={(yScale(row.cohort_week) ?? 0) + cellH / 2 + 12}
                fontSize={9}
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--text-tertiary)"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {formatInt(row.cohort_size)} users
              </text>
            </g>
          ))}

          {/* Cells */}
          {cohorts.map((row) => {
            const y = yScale(row.cohort_week) ?? 0;
            return row.cells.map((cell) => {
              const x = xScale(cell.week_offset) ?? 0;
              const color = retentionColor(cell.retention_pct);
              const textColor = cell.retention_pct >= 50 ? 'var(--surface)' : 'var(--text-graphite)';
              return (
                <g
                  key={`${row.cohort_week}-${cell.week_offset}`}
                  onMouseLeave={hideTooltip}
                  onMouseMove={(e) => {
                    const target = e.currentTarget as SVGGElement;
                    const bbox = target.getBoundingClientRect();
                    showTooltip({
                      tooltipLeft: e.clientX - bbox.left + x + MARGIN.left,
                      tooltipTop: y + MARGIN.top,
                      tooltipData: {
                        cohort_week: row.cohort_week,
                        cohort_size: row.cohort_size,
                        week_offset: cell.week_offset,
                        active_users: cell.active_users,
                        retention_pct: cell.retention_pct,
                      },
                    });
                  }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={cellW}
                    height={cellH}
                    rx={3}
                    fill={color}
                    stroke="var(--surface)"
                    strokeWidth={1}
                  />
                  {cellW >= 28 && cellH >= 20 && (
                    <text
                      x={x + cellW / 2}
                      y={y + cellH / 2}
                      fontSize={10}
                      fontWeight={600}
                      fontFamily="Inter, system-ui, sans-serif"
                      fill={textColor}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      pointerEvents="none"
                    >
                      {cell.retention_pct.toFixed(0)}
                    </text>
                  )}
                </g>
              );
            });
          })}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            background: 'var(--text-primary)',
            color: 'var(--surface)',
            padding: '8px 10px',
            borderRadius: 6,
            fontSize: 11,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          <div className="font-semibold">
            {formatCohortLabel(tooltipData.cohort_week)} cohort · Week {tooltipData.week_offset}
          </div>
          <div className="mt-1 text-gray-300">
            {formatPercent(tooltipData.retention_pct)} retained
          </div>
          <div className="text-gray-300">
            {formatInt(tooltipData.active_users)} of {formatInt(tooltipData.cohort_size)} active
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};
